// src/components/oracle/DailyPortalOracleDeck.js

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { oracleDeck, uiText } from '../../data/oracle/oracleDeckData';
import { useGlow } from '../../hooks/useGlow';
import { SparklesIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { getOracleReading } from '../../services/geminiService';
import AIReadingDisplay from './AIReadingDisplay';
import OracleCardDetail from './OracleCardDetail';
import OracleCreditManager from './OracleCreditManager';
import CooldownTimer from '../common/CooldownTimer';

const STORAGE_KEY = 'dailyOracleCard';
const AI_DAYS_LIMIT = 3; // Cooldown de 3 días para la lectura de IA

// Helper para verificar si la IA sigue en periodo de espera
const checkIsAiOnCooldown = (lastAiDateIso, limitDays = AI_DAYS_LIMIT) => {
  if (!lastAiDateIso) return false;
  const lastTime = new Date(lastAiDateIso).getTime();
  const now = Date.now();
  const cooldownMs = limitDays * 24 * 60 * 60 * 1000;
  return (now - lastTime) < cooldownMs;
};

// Componente para representar el mazo de cartas antes de la selección
const DeckPile = ({ onDraw, isDrawing, ui }) => (
  <div className="flex flex-col items-center justify-center text-center p-8 min-h-[60vh] animate-fade-in">
    <h2 className="text-3xl md:text-4xl font-bold text-gray-200 mb-4">{ui.dailyCardTitle}</h2>
    <p className="text-lg text-gray-400 mb-8 max-w-md">{ui.dailyCardIntro}</p>
    
    <button
      onClick={onDraw}
      disabled={isDrawing}
      className="relative group w-48 h-64 perspective-1000"
      aria-label={ui.drawCardAction}
    >
      {[...Array(3)].map((_, i) => (
        <img
          key={i}
          src={oracleDeck[0].cardBack}
          alt="Reverso de una carta del oráculo"
          className={`absolute inset-0 w-full h-full object-cover rounded-xl border-2 border-purple-400/30 shadow-lg
                      transition-all duration-500 ease-in-out group-hover:shadow-purple-500/50
                      ${isDrawing ? 'animate-pulse' : 'group-hover:-translate-y-2'}`}
          style={{
            transform: `rotateZ(${i * 4 - 4}deg) translateX(${i * 2 - 2}px)`,
            zIndex: 3 - i,
          }}
        />
      ))}
    </button>
    <p className="text-sky-300 mt-8 font-semibold animate-pulse">
      {isDrawing ? ui.drawingCard : ui.drawCardAction}
    </p>
  </div>
);

const DailyPortalOracleDeck = ({ lang = 'es' }) => {
  const [todayCard, setTodayCard] = useState(null);
  const [isRevealed, setIsRevealed] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);

  // Fecha de la última lectura IA (para el CooldownTimer de 3 días)
  const [lastAiReadingDate, setLastAiReadingDate] = useState(null);

  // Estados para la lectura de IA
  const [aiReading, setAiReading] = useState('');
  const [isLoadingAi, setIsLoadingAi] = useState(false);
  const hasTriggeredAiRef = useRef(false);

  const ui = uiText[lang] || uiText['es'];
  const titleStyle = useGlow({ gradient: 'linear-gradient(to bottom, #e0e0e0ff, #9b977aff, #9e823cff, #776f5cff)' });

  const dailyTopic = lang === 'en' ? 'Daily Energy & Guidance' : 'Energía y Guía del Día';
  const spreadTitle = lang === 'en' ? 'Card of the Day' : 'Carta del Día';

  // Determinar si la lectura con IA está bloqueada por cooldown
  const isAiLocked = checkIsAiOnCooldown(lastAiReadingDate, AI_DAYS_LIMIT);

  // EFECTO 1: Comprobar localStorage (carta del día vs fecha de la lectura de IA)
  useEffect(() => {
    const checkLocalStorage = () => {
      try {
        const rawData = localStorage.getItem(STORAGE_KEY);
        if (rawData) {
          const parsed = JSON.parse(rawData);
          const { cardId, cardDate, reading, lastAiDate } = parsed;
          
          // Preservar la fecha del cooldown de IA si existe
          if (lastAiDate) {
            setLastAiReadingDate(lastAiDate);
          }

          // Verificar si la CARTA fue sacada hoy
          const todayPrefix = new Date().toISOString().split('T')[0];
          const isCardFromToday = typeof cardDate === 'string' && cardDate.startsWith(todayPrefix);

          if (isCardFromToday) {
            const card = oracleDeck.find(c => c.id === cardId);
            if (card) {
              setTodayCard(card);
              setTimeout(() => setIsRevealed(true), 100);

              if (reading) {
                setAiReading(reading);
                hasTriggeredAiRef.current = true;
              }
            }
          } else {
            // Ya es otro día: reseteamos la carta para que pueda sacar una nueva,
            // pero MANTENEMOS lastAiDate en localStorage para no perder el cooldown de IA
            localStorage.setItem(STORAGE_KEY, JSON.stringify({ lastAiDate: lastAiDate || null }));
            setTodayCard(null);
            setAiReading('');
            hasTriggeredAiRef.current = false;
          }
        }
      } catch (error) {
        console.error("Error reading from localStorage:", error);
      }
    };
    checkLocalStorage();
  }, []);

  // Función para consultar a la IA
  const handleGetDailyAIReading = useCallback(async (cardToRead = todayCard) => {
    // Si la IA está en cooldown o ya está cargando, no disparar
    if (!cardToRead || isLoadingAi || isAiLocked) return;

    setIsLoadingAi(true);
    const cardData = cardToRead[lang];

    const cardForAi = [{
      name: `${cardData.name} (${cardData.name2 || ''}, ${cardData.name3 || ''}) - Categoría: ${cardData.category || ''}`,
      positionName: dailyTopic,
      isReversed: false,
      breaf: cardData.breaf || cardData.description || '',
      keywords: cardData.keywords || []
    }];

    try {
      const response = await getOracleReading(dailyTopic, spreadTitle, cardForAi, lang);
      
      if (response && response.success) {
        const nowIso = new Date().toISOString();
        setAiReading(response.text);
        setLastAiReadingDate(nowIso);

        // Guardar lectura y registrar la fecha de la lectura para el cooldown de 3 días
        const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
        stored.reading = response.text;
        stored.lastAiDate = nowIso;
        localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
      }
    } catch (err) {
      console.error("Error getting daily AI reading:", err);
    } finally {
      setIsLoadingAi(false);
    }
  }, [todayCard, isLoadingAi, isAiLocked, lang, dailyTopic, spreadTitle]);

  // EFECTO 2: Disparo automático de la IA SOLO SI NO ESTÁ EN COOLDOWN
  useEffect(() => {
    if (todayCard && isRevealed && !aiReading && !isLoadingAi && !hasTriggeredAiRef.current) {
      if (isAiLocked) {
        // Está en cooldown: no se dispara la lectura
        return;
      }

      hasTriggeredAiRef.current = true;
      const timer = setTimeout(() => {
        handleGetDailyAIReading(todayCard);
      }, 700);

      return () => clearTimeout(timer);
    }
  }, [todayCard, isRevealed, aiReading, isLoadingAi, isAiLocked, handleGetDailyAIReading]);

  // Función para sacar carta diaria
  const handleDrawCard = () => {
    if (isDrawing) return;
    setIsDrawing(true);
    hasTriggeredAiRef.current = false;
    setAiReading('');

    setTimeout(() => {
      const randomIndex = Math.floor(Math.random() * oracleDeck.length);
      const selectedCard = oracleDeck[randomIndex];
      const nowIso = new Date().toISOString();
      
      // Preservamos el lastAiDate que ya existía en localStorage
      let existingLastAiDate = lastAiReadingDate;
      try {
        const prev = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
        if (prev.lastAiDate) existingLastAiDate = prev.lastAiDate;
      } catch (e) {}

      const dataToStore = { 
        cardId: selectedCard.id, 
        cardDate: nowIso,
        reading: '',
        lastAiDate: existingLastAiDate || null
      };

      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(dataToStore));
      } catch (error) {
        console.error("Error writing to localStorage:", error);
      }

      setTodayCard(selectedCard);

      setTimeout(() => {
        setIsRevealed(true);
        setIsDrawing(false);
      }, 700);
    }, 2000);
  };

  // Reset al usar un crédito: habilita la lectura inmediata
  const handleCreditUsed = () => {
    // Al gastar crédito, reiniciamos el cooldown de la IA
    setLastAiReadingDate(null);
    setAiReading('');
    hasTriggeredAiRef.current = false;

    try {
      const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
      stored.lastAiDate = null;
      stored.reading = '';
      localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
    } catch (e) {}

    // Si ya hay una carta en pantalla, disparamos la lectura directamente
    if (todayCard) {
      handleGetDailyAIReading(todayCard);
    }
  };

  // VISTA 1: La carta del día ya ha sido seleccionada
  if (todayCard) {
    const cardContent = todayCard[lang];
    const cardForDisplay = [{
      ...todayCard,
      data: cardContent,
      positionName: dailyTopic,
      isReversed: false,
      isRevealed: true
    }];

    return (
      <div className="w-full max-w-4xl mx-auto p-4 md:p-8 text-white flex flex-col items-center animate-fade-in">
        <header className="w-full text-center mb-8">
            <h1 style={titleStyle} className="text-3xl md:text-5xl font-bold">{ui.yourDailyCard}</h1>
            <p className="text-gray-400 text-sm mt-2">{dailyTopic}</p>
        </header>

        {/* Detalle visual y simbólico de la carta (SIEMPRE DISPONIBLE DIARIAMENTE) */}
        <OracleCardDetail lang={lang} card={todayCard} />

        {/* Sección de la Lectura de IA y Temporizador */}
        <section className="w-full max-w-3xl mt-12 flex flex-col items-center gap-8 transition-all duration-700">
          <div className="w-full">
            {isLoadingAi || aiReading ? (
              /* Caso A: Se generó o se está generando la lectura de IA */
              <AIReadingDisplay 
                interpretation={aiReading} 
                isLoading={isLoadingAi} 
                topic={dailyTopic}
                cards={cardForDisplay}
                spreadName={spreadTitle}
              />
            ) : isAiLocked ? (
              /* Caso B: Carta visible, pero la IA aún está en cooldown de 3 días */
              <div className="bg-gray-900/60 border border-purple-500/20 rounded-2xl p-6 text-center shadow-lg">
                <div className="inline-flex p-3 rounded-full bg-purple-900/30 text-purple-300 mb-3">
                  <LockClosedIcon className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-semibold text-gray-200">
                  {lang === 'en' ? 'Oracle Reading Recharging' : 'Canalización en Reposo'}
                </h3>
                <p className="text-sm text-gray-400 mt-1 max-w-md mx-auto">
                  {lang === 'en'
                    ? 'You can reflect on today’s card above. The extended AI oracle guidance will recharge soon.'
                    : 'Puedes conectar con la energía de tu carta diaria de hoy. La interpretación profunda con IA estará disponible al terminar el periodo de recarga.'}
                </p>
              </div>
            ) : (
              /* Caso C: Cooldown expirado, botón para pedir lectura si no disparó auto */
              <div className="flex justify-center mt-6">
                <button 
                  onClick={() => handleGetDailyAIReading(todayCard)}
                  className="bg-purple-600 hover:bg-purple-500 text-white font-semibold py-3 px-8 rounded-full shadow-lg shadow-purple-900/50 transition-all flex items-center gap-2"
                >
                  <SparklesIcon className="w-5 h-5" />
                  {lang === 'en' ? 'Get AI Interpretation' : 'Canalizar Mensaje del Oráculo'}
                </button>
              </div>
            )}
          </div>

          {/* Temporizador de Cooldown: usa la fecha de la última lectura y 3 días de límite */}
          {lastAiReadingDate && isAiLocked && (
            <CooldownTimer
              startDate={lastAiReadingDate}
              daysLimit={AI_DAYS_LIMIT}
              lang={lang}
            />
          )}

          {/* Administrador de Créditos para saltar el cooldown */}
          <footer className="w-full pt-8 border-t border-gray-800 flex flex-col items-center mb-8">
            <OracleCreditManager 
              lang={lang}
              onCreditUsed={handleCreditUsed}
            />
          </footer>
        </section>
      </div>
    );
  }

  // VISTA 2: Aún no se ha sacado la carta del día
  return <DeckPile onDraw={handleDrawCard} isDrawing={isDrawing} ui={ui} />;
};

export default DailyPortalOracleDeck;